/**
 * @module billing/stripeCheckout
 * @description Stripe Checkout session creation and redirect helpers.
 * @extracted-from client/src/components/Paywall.js
 */

import { loadStripe } from '@stripe/stripe-js';

import { API_BASE_URL } from '../apiConfig';
import analytics from '../utils/analytics';
import { apiFetch } from '../api/authHeaders';
import { PRICING_TIERS, SUBSCRIPTION_PLANS } from '../constants/pricing';

export function getStripeCheckoutUrls() {
  if (typeof window === 'undefined') {
    return { successUrl: 'https://criticai.ca/success', cancelUrl: 'https://criticai.ca/' };
  }
  const origin = window.location.origin.replace(/\/$/, '');
  return {
    successUrl: `${origin}/success`,
    cancelUrl: `${origin}/`,
  };
}

async function initializeStripe() {
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  if (!publishableKey || publishableKey.length < 20) {
    console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is missing or invalid');
    return null;
  }

  try {
    const stripePromise = loadStripe(publishableKey);
    const stripe = await Promise.race([
      stripePromise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('Stripe load timeout')), 5000)),
    ]);
    return stripe;
  } catch (error) {
    console.error('Failed to initialize Stripe:', error);
    return null;
  }
}

/**
 * Creates a Stripe Checkout session and redirects the browser to Stripe.
 * @param {Object} params
 * @param {'one-time'|'subscription'} params.paymentType
 * @param {string} [params.selectedTier='pay_per_use']
 * @param {number} [params.ppuQuantity=1]
 * @param {string} [params.selectedSubTier='200'] — SUBSCRIPTION_PLANS id
 * @param {object|null} [params.appliedCoupon] — validated coupon from `/api/validate-coupon`
 * @param {{ email?: string, id?: string }|null} [params.accountUser]
 */
export async function startStripeCheckout({
  paymentType,
  selectedTier = 'pay_per_use',
  ppuQuantity = 1,
  selectedSubTier = '200',
  appliedCoupon = null,
  accountUser = null,
}) {
  let stripe = await initializeStripe();
  if (!stripe) {
    stripe = await initializeStripe();
  }
  if (!stripe) {
    throw new Error('Payment system is not available. Please refresh the page and try again.');
  }

  const { successUrl, cancelUrl } = getStripeCheckoutUrls();

  let response;

  if (paymentType === 'subscription') {
    const selectedPlan =
      SUBSCRIPTION_PLANS.find((plan) => plan.id === selectedSubTier) || SUBSCRIPTION_PLANS[0];

    if (!accountUser?.email || !accountUser?.id) {
      throw new Error(
        'Please sign in with Google (use the button in the header) so we can attach your subscription to your account.'
      );
    }

    analytics.trackCheckoutStarted({
      paymentType: 'subscription',
      tierId: selectedPlan.id,
      tierName: selectedPlan.name,
      quantity: 1,
      value: selectedPlan.price,
      currency: 'USD',
      couponCode: null,
    });
    analytics.trackAddPaymentInfo('subscription', selectedPlan.id, selectedPlan.price, 'USD');

    response = await apiFetch(`${API_BASE_URL}/api/create-subscription-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        successUrl,
        cancelUrl,
        userEmail: accountUser.email,
        userId: accountUser.id,
        subscriptionTier: selectedSubTier,
      }),
    });
  } else {
    const tier = PRICING_TIERS.find((t) => t.id === selectedTier);
    if (!tier) {
      throw new Error('Invalid pricing tier selected');
    }
    const qty = Math.min(5000, Math.max(1, parseInt(String(ppuQuantity), 10) || 1));
    const priceUsd = Math.round(qty * 100) / 100;

    analytics.trackCheckoutStarted({
      paymentType: 'one-time',
      tierId: tier.id,
      tierName: tier.name,
      quantity: qty,
      value: priceUsd,
      currency: 'USD',
      couponCode: appliedCoupon ? appliedCoupon.id : null,
    });
    analytics.trackAddPaymentInfo('one-time', tier.id, priceUsd, 'USD');

    response = await apiFetch(`${API_BASE_URL}/api/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tier: tier.id,
        quantity: qty,
        price: priceUsd,
        couponCode: appliedCoupon ? appliedCoupon.id : null,
        successUrl,
        cancelUrl,
      }),
    });
  }

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      throw new Error(`Server returned ${response.status} ${response.statusText}`);
    }
    throw new Error(errorData.error || 'Failed to create checkout session');
  }

  let session;
  try {
    session = await response.json();
  } catch {
    throw new Error('Invalid response from server. Please try again.');
  }

  if (session.error) {
    throw new Error(session.error);
  }

  const result = await stripe.redirectToCheckout({
    sessionId: session.sessionId,
  });

  if (result.error) {
    throw new Error(result.error.message);
  }
}
